import {FC, memo, useEffect, useMemo, useRef, useState} from 'react';
import {hasValue, isSameDayEx, IChatMessageData, LocalEventEmitter} from '@kcast-fe/core';
import {Box, Flex} from '@chakra-ui/react';

import {
  DayInfo,
  DotsMenu,
  WorkedFor,
  ThreadInfo,
  RenderMarkdown,
  RenderFileList,
  RenderImageList,
  RenderVideoList,
  WrapperForTextType
} from './components';

interface IProps {
  index: number;
  msg: IChatMessageData;
  onVisibilityChanged: (idx: number, isVisible: boolean) => void;
}

const MessageItemFC: FC<IProps> = ({index, msg, onVisibilityChanged}) => {
  const [safePrevThreadId, setSafePrevThreadId] = useState(msg.prevThreadId);
  const [safeThreadId, setSafeThreadId] = useState(msg.thread_id);
  const [safeIsFinal, setSafeIsFinal] = useState(msg.is_final);
  const [safeText, setSafeText] = useState(msg.text);
  const [safeDuration, setSafeDuration] = useState(msg.response_duration_ms);

  const refIndex = useRef(index);
  const ref = useRef<HTMLDivElement | null>(null);

  const isMyMsg = useMemo(() => {
    return msg.actor_kind === 'user';
  }, [msg.actor_kind]);

  const isNewDay = useMemo(() => {
    return !msg.prevCreatedAt || !isSameDayEx(msg.created_at, msg.prevCreatedAt);
  }, [msg.created_at, msg.prevCreatedAt]);

  const isMsgHasText = useMemo(() => {
    return hasValue(msg.text);
  }, [msg.text]);

  const files = useMemo(
    () => msg.attachments?.filter((at) => at.type === 'file') ?? [],
    [msg.attachments]
  );

  const images = useMemo(
    () => msg.attachments?.filter((at) => at.type === 'image') ?? [],
    [msg.attachments]
  );

  const videos = useMemo(
    () => msg.attachments?.filter((at) => at.type === 'video') ?? [],
    [msg.attachments]
  );

  useEffect(() => {
    const indexValue = refIndex.current;
    const observer = new IntersectionObserver(([entry]) =>
      onVisibilityChanged(indexValue, entry.isIntersecting)
    );
    if (ref.current) {
      observer.observe(ref.current);
    }
    return () => {
      onVisibilityChanged(indexValue, false);
      observer.disconnect();
    };
  });

  useEffect(() => {
    setSafeIsFinal(msg.is_final);
  }, [msg.is_final]);

  useEffect(() => {
    setSafeText(msg.text);
  }, [msg.text]);

  useEffect(() => {
    setSafeDuration(msg.response_duration_ms);
  }, [msg.response_duration_ms]);

  useEffect(() => {
    const onMsgChunk = (data: IChatMessageData) => {
      if (data.id === msg.id) {
        setSafeText(data.text);
        setSafeIsFinal(data.is_final);
        setSafeDuration(data.response_duration_ms);
      }
    };
    if (!msg.is_final) {
      LocalEventEmitter.on('MsgTextChunk', onMsgChunk);
    }
    return () => {
      LocalEventEmitter.off('MsgTextChunk', onMsgChunk);
    };
  }, [msg.id, msg.is_final]);

  useEffect(() => {
    const onMsgThread = (msgId: string, threadId?: string, prevThreadId?: string) => {
      if (msgId === msg.id) {
        setSafeThreadId(threadId);
        setSafePrevThreadId(prevThreadId);
      }
    };
    LocalEventEmitter.on('MsgThreadChanged', onMsgThread);
    return () => {
      LocalEventEmitter.off('MsgThreadChanged', onMsgThread);
    };
  }, [msg.id]);

  return (
    <Box ref={ref} pt={'24px'} data-test-id={msg.id}>
      <Box w={'100%'} p={{base: '0 70px', smToMd: '0 16px'}}>
        {isNewDay && <DayInfo createdAt={msg.created_at} />}

        <ThreadInfo isFinal={safeIsFinal} threadId={safeThreadId} prevThreadId={safePrevThreadId} />

        {msg.actor_kind !== 'user' && safeIsFinal && hasValue(safeDuration) && (
          <Flex p={'16px 0 0 0'}>
            <WorkedFor messageId={msg.id} durationMs={safeDuration} />
          </Flex>
        )}

        {files.length > 0 && (
          <Flex p={'16px 0 0 0'} justifyContent={isMyMsg ? 'end' : 'start'}>
            <RenderFileList isMyMsg={isMyMsg} files={files} />
          </Flex>
        )}

        <Flex
          p={'16px 0 0 0'}
          pos={'relative'}
          justifyContent={isMyMsg ? 'end' : 'start'}
          data-testid={'message-item'}
          className={'group'}
        >
          {/* TEXT MESSAGE */}
          {msg.type === 'text' && (
            <WrapperForTextType
              isMyMsg={isMyMsg}
              isMsgHasText={isMsgHasText}
              isMsgHasImages={images.length > 0}
              isMsgHasVideos={videos.length > 0}
              createdAt={msg.created_at}
            >
              {!isMyMsg && <DotsMenu msgId={msg.id} isFinal={safeIsFinal} containerRef={ref} />}
              <RenderImageList images={images} isMyMsg={isMyMsg} isMsgHasText={isMsgHasText} />
              <RenderVideoList videos={videos} />

              {hasValue(safeText) && (
                <RenderMarkdown
                  text={safeText}
                  isMyMsg={isMyMsg}
                  isFinal={safeIsFinal}
                  withPadding={images.length > 0 || videos.length > 0}
                />
              )}
            </WrapperForTextType>
          )}
        </Flex>
      </Box>
    </Box>
  );
};

export const MessageItem = memo(MessageItemFC);
